import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Save, Upload } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function SettingsPage() {
  const [settings, setSettings] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then(r => setSettings(r.data)).catch(() => {});
  }, []);

  const set = (k, v) => setSettings(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put("/settings", settings);
      toast.success("Settings saved!");
    } catch { toast.error("Failed to save"); }
    setSaving(false);
  };

  const handleUpload = async (e, key) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await api.post("/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
      set(key, res.data.url);
      toast.success("Uploaded!");
    } catch { toast.error("Upload failed"); }
  };

  return (
    <div data-testid="settings-page">
      <div className="mb-6">
        <h1 className="text-3xl font-serif font-bold text-foreground">Site Settings</h1>
        <p className="text-sm font-sans text-muted-foreground mt-1">Manage branding, theme, and site configuration</p>
      </div>

      <div className="max-w-2xl space-y-8">
        {/* Branding */}
        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-6 space-y-5">
          <h2 className="text-xl font-serif font-bold text-foreground">Branding</h2>

          <div>
            <Label className="font-sans text-sm">Site Name</Label>
            <Input value={settings.site_name || ""} onChange={e => set("site_name", e.target.value)} className="mt-1" data-testid="setting-site-name" />
          </div>

          <div>
            <Label className="font-sans text-sm">Tagline</Label>
            <Input value={settings.tagline || ""} onChange={e => set("tagline", e.target.value)} className="mt-1" data-testid="setting-tagline" />
          </div>

          <div>
            <Label className="font-sans text-sm">Logo URL</Label>
            <div className="flex gap-2 mt-1">
              <Input value={settings.logo_url || ""} onChange={e => set("logo_url", e.target.value)} placeholder="https://... or upload" className="flex-1" data-testid="setting-logo-url" />
              <label className="cursor-pointer">
                <input type="file" accept="image/*" className="hidden" onChange={e => handleUpload(e, "logo_url")} />
                <Button type="button" variant="outline" size="icon" className="h-10 w-10" asChild>
                  <span><Upload className="h-4 w-4" /></span>
                </Button>
              </label>
            </div>
            {settings.logo_url && <img src={settings.logo_url} alt="Logo" className="mt-2 h-12 object-contain" />}
          </div>
        </div>

        <Separator />

        {/* Theme */}
        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-6 space-y-5">
          <h2 className="text-xl font-serif font-bold text-foreground">Theme</h2>
          <div className="flex items-center gap-2">
            <Switch
              checked={settings.theme === "dark"}
              onCheckedChange={v => set("theme", v ? "dark" : "light")}
              data-testid="setting-theme-toggle"
            />
            <Label className="font-sans text-sm">Dark Mode Default</Label>
          </div>
        </div>

        <Button onClick={handleSave} disabled={saving} className="bg-[#1E88E5] hover:bg-[#1565C0] font-sans" data-testid="save-settings">
          <Save className="h-4 w-4 mr-2" /> {saving ? "Saving..." : "Save Settings"}
        </Button>
      </div>
    </div>
  );
}
